import { forwardRef } from 'react'

import { tv } from 'tailwind-variants'

export type TSwitchSize = 'sm' | 'md' | 'lg'

export type TSwitchProps = {
  text?: string
  size?: TSwitchSize
} & Pick<
  React.InputHTMLAttributes<HTMLInputElement>,
  'id' | 'name' | 'checked' | 'onChange' | 'disabled'
>

export const switchVariants = tv({
  slots: {
    labelSlot: 'inline-flex items-center',
    containeSlot: 'relative',
    inputSlot: 'peer sr-only',
    trackSlot:
      'font-body flex items-center rounded-full transition-colors duration-200 ease-in-out',
    thumbSlot:
      'bg-surface-white-50 rounded-full transition-all duration-200 ease-in-out',
    textSlot: 'font-body'
  },
  variants: {
    size: {
      sm: {
        labelSlot: 'gap-2',
        trackSlot: 'h-[11px] w-[21px] px-1',
        thumbSlot: 'h-[6.25px] w-[6.25px]',
        textSlot: 'text-caption-lg'
      },
      md: {
        labelSlot: 'gap-2',
        trackSlot: 'p-1.25 h-[14px] w-[28px]',
        thumbSlot: 'h-[7.25px] w-[7.25px]',
        textSlot: 'text-body-md'
      },
      lg: {
        labelSlot: 'gap-3',
        trackSlot: 'px-1.25 h-[18px] w-[35px]',
        thumbSlot: 'h-[9.25px] w-[9.25px]',
        textSlot: 'text-body-lg'
      }
    },
    checked: {
      true: {
        trackSlot: 'bg-surface-black-50 justify-end',
        textSlot: 'text-surface-black-50 font-bold'
      },
      false: {
        trackSlot: 'bg-surface-white-400 justify-start',
        textSlot: 'text-surface-black-50 font-normal'
      }
    },
    disabled: {
      true: {
        labelSlot: 'cursor-not-allowed',
        trackSlot: 'bg-surface-white-400',
        textSlot: 'text-surface-black-500'
      },
      false: {
        labelSlot: 'cursor-pointer',
        trackSlot: 'cursor-pointer'
      }
    }
  },
  defaultVariants: {
    size: 'md',
    checked: false,
    disabled: false
  }
})

/**
 * @deprecated Use `Switch` from `@alternative/acqua/molecules` instead. The acqua-ui Switch is being phased out in favor of the acqua design system.
 */
export const Switch = forwardRef<HTMLInputElement, TSwitchProps>(
  (
    {
      text,
      checked = false,
      onChange,
      size = 'md',
      disabled = false,
      id,
      name,
      ...props
    }: TSwitchProps,
    ref
  ) => {
    const {
      labelSlot,
      containeSlot,
      trackSlot,
      thumbSlot,
      inputSlot,
      textSlot
    } = switchVariants({
      size,
      checked,
      disabled
    })

    return (
      <div className={containeSlot()}>
        <label className={labelSlot()} htmlFor={id}>
          <input
            type='checkbox'
            checked={checked}
            id={id}
            ref={ref}
            name={name}
            onChange={onChange}
            readOnly={!onChange}
            disabled={disabled}
            className={inputSlot()}
            {...props}
          />
          <span className={trackSlot()}>
            <div className={thumbSlot()} />
          </span>
          {text && (
            <span className={textSlot()} data-testid='switch-text'>
              {text}
            </span>
          )}
        </label>
      </div>
    )
  }
)

Switch.displayName = 'Switch'
